<template>
  <v-card flat class="yt-card">
    <div class="yt-card__header">
      <div class="yt-card__heading">
        <h3 class="yt-card__title">{{ title }}</h3>
        <span v-if="subtitle" class="yt-card__subtitle">{{ subtitle }}</span>
      </div>
      <div class="yt-card__tools">
        <span v-for="source in sources" :key="source" :class="['yt-src', `yt-src--${source}`]">
          {{ sourceLabels[source] || source }}
        </span>
        <slot name="tools" />
        <rmr-segmented-control
          v-if="columns.length"
          :value="mode"
          :items="modeItems"
          size="sm"
          @input="mode = $event"
        />
      </div>
    </div>
    <div v-if="loading" class="yt-card__placeholder">
      <v-progress-circular indeterminate :color="indigo" size="28" width="3" />
    </div>
    <p v-else-if="empty" class="yt-card__empty">{{ emptyText }}</p>
    <div v-else-if="mode === 'table' && columns.length" class="yt-table-wrap">
      <table class="yt-table">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key" :class="{ 'yt-table__num': column.numeric !== false }">
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="index">
            <td v-for="column in columns" :key="column.key" :class="{ 'yt-table__num': column.numeric !== false }">
              {{ column.format ? column.format(row[column.key], row) : row[column.key] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="yt-card__body">
      <slot />
    </div>
  </v-card>
</template>

<script>
import { mdiChartLine, mdiTable } from '@mdi/js'
import { ref } from '@vue/composition-api'
import RmrSegmentedControl from '@/components/ui/actions/RmrSegmentedControl.vue'
import { RMR_INDIGO } from '@/styles/tokens'

const SOURCE_LABELS = { yt: 'YouTube Analytics', ytdata: 'YouTube Data', gsc: 'Search Console', calc: 'Estimation' }

export default {
  name: 'YtChartCard',
  components: { RmrSegmentedControl },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    sources: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    empty: { type: Boolean, default: false },
    emptyText: { type: String, default: 'Aucune donnée pour ces filtres.' },
  },
  setup() {
    const mode = ref('chart')
    const modeItems = [
      { value: 'chart', icon: mdiChartLine, title: 'Graphique' },
      { value: 'table', icon: mdiTable, title: 'Tableau' },
    ]
    return { mode, modeItems, sourceLabels: SOURCE_LABELS, indigo: RMR_INDIGO }
  },
}
</script>

<style lang="scss" scoped>
.yt-card {
  border-radius: $rmr-radius-2xl !important;
  padding: $rmr-space-lg $rmr-space-xl;
  box-shadow: $rmr-shadow-card !important;
  display: flex;
  flex-direction: column;
  gap: $rmr-space-sm;
  min-width: 0;
  height: 100%;
}

.yt-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $rmr-space-sm;
  flex-wrap: wrap;
}

.yt-card__title {
  font-size: $rmr-fs-title;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-indigo-dark;
  letter-spacing: 0.5px;
  margin: 0;
}

.yt-card__subtitle {
  display: block;
  font-size: $rmr-fs-base;
  color: $rmr-text-subtle;
  font-weight: 500;
  margin-top: 4px;
  max-width: 70ch;
}

.yt-card__tools {
  display: flex;
  align-items: center;
  gap: $rmr-space-sm;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.yt-src {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: $rmr-fs-xs;
  font-weight: 600;
  color: $rmr-text-muted;
  white-space: nowrap;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $rmr-ca;
  }

  &--gsc::before { background: $rmr-sessions; }
  &--calc::before { background: $rmr-text-subtle; }
  &--ytdata::before { background: $rmr-ca-dark; }
}

.yt-card__body {
  min-width: 0;
  flex: 1;
}

.yt-card__placeholder,
.yt-card__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-md;
  margin: 0;
  background: $rmr-surface-2;
  border-radius: $rmr-radius-lg;
}

.yt-table-wrap {
  overflow: auto;
  max-height: 420px;
}
</style>

<style lang="scss">
.yt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: $rmr-fs-base;

  th,
  td {
    padding: 6px 10px;
    border-bottom: 1px solid $rmr-border;
    text-align: left;
    white-space: nowrap;
  }

  th {
    position: sticky;
    top: 0;
    background: $rmr-surface-2;
    font-size: $rmr-fs-xs;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $rmr-text-muted;
  }

  td { color: $rmr-text-primary; }

  .yt-table__num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.yt-table__link { cursor: pointer; }
  tr.yt-table__link:hover td { background: $rmr-indigo-bg; }
}
</style>
